import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import dayjs from 'dayjs';
import time from 'utils/time';
import DescriptionTinyEditor from 'components/Dashboard/UserContent/ui/DescriptionTinyEditor';
import { CalendarPopup } from 'components/Dashboard/Filters/ui/DateFilter';
import DropdownComponent from 'components/Settings/WebsiteFeeds/DropdownComponent';
import { store } from 'components/Store';
import PeopleAsyncSelect from './PeopleAsyncSelect';
import MembersSelect from './MembersSelect';

export const INTERACTION_TYPES = ['Email', 'Meeting', 'Phone call', 'Social media', 'Events', 'Other'];

export const InteractionFormFieldsSkeleton = () => (
  <>
    <div className='d-flex flex-column flex-sm-row mb-3'>
      <div style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={60} className='mb-1' />
        <Skeleton height={38} />
      </div>
      <div className='mt-3 mt-sm-0 ml-sm-3' style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={60} className='mb-1' />
        <Skeleton height={38} />
      </div>
    </div>
    <div className='d-flex flex-column flex-sm-row mb-3'>
      <div style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={60} className='mb-1' />
        <Skeleton height={38} />
      </div>
      <div className='mt-3 mt-sm-0 ml-sm-3' style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={80} className='mb-1' />
        <Skeleton height={38} />
      </div>
    </div>
    <div className='d-flex flex-column flex-sm-row mb-3'>
      <div style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={140} className='mb-1' />
        <Skeleton height={176} />
      </div>
      <div className='mt-3 mt-sm-0 ml-sm-3' style={{ minWidth: 0, flex: '1 1 0%' }}>
        <Skeleton height={19} width={100} className='mb-1' />
        <Skeleton height={176} />
      </div>
    </div>
    <div className='mb-3'>
      <Skeleton height={19} width={100} className='mb-2' />
      <Skeleton height={200} />
    </div>
  </>
);

const RemoveSelectionButton = ({ id, count, singular, plural, onRemoveAll, openDropdown, setOpenDropdown }) => {
  if (!count) return null;
  const isOpen = openDropdown === id;
  return (
    <div className='position-relative d-inline-block flex-shrink-0'>
      <button
        type='button'
        className='general-button circle-button hover-background-gray-05 p-1'
        onClick={() => setOpenDropdown((d) => (d === id ? null : id))}
      >
        <span className='icon-three-dots text-main-blue-40' />
      </button>
      {isOpen && (
        <>
          <div className='general-close-background z-max' onClick={() => setOpenDropdown(null)} />
          <div className='crm-actions-box box box-shadow specific-top-padding-6 specific-bottom-padding-6'>
            <button
              type='button'
              className='general-button specific-right-padding-35 specific-left-padding-16 d-flex align-items-center list-action-button'
              onClick={() => {
                onRemoveAll();
                setOpenDropdown(null);
              }}
            >
              <span className='icon-remove-stakeholders text-dark-red' />
              <span className='specific-left-padding-14 text-dark-red'>
                {count === 1 ? `Remove this ${singular}` : `Remove all ${count} ${plural}`}
              </span>
            </button>
          </div>
        </>
      )}
    </div>
  );
};

const InteractionFormFields = ({ form, onChange, autoFocusTitle = false }) => {
  const { state } = useContext(store);
  const teamName = state.team?.teamName ?? 'Organisation';
  const organisationName = state.activePlan?.organisationName ?? teamName;

  const [calendarState, setCalendarState] = useState({
    active: false,
    filterProperty: '',
    selectedDay: form.date ? time.parseCalendarDateString(form.date).toDate() : new Date(),
    name: '',
    range: { from: '', to: '' },
  });

  const [openDropdown, setOpenDropdown] = useState(null);
  const onMenuCloseFor = (id) => () => setOpenDropdown((d) => (d === id ? null : d));

  // Shared close signal for every DropdownComponent instance: bumped only on scroll, so it's
  // unrelated to which dropdown (if any) is currently open. Folding `openDropdown` into this used
  // to make a dropdown close itself right after opening whenever a DIFFERENT dropdown had been open
  // just before (its own id-keyed signal value changed on that same click), requiring a second click.
  const [scrollCloseTick, setScrollCloseTick] = useState(0);

  const formTopRef = useRef(null);
  useEffect(() => {
    // DropdownComponent's fixedDropdown menus are position:fixed, calculated once on open, so they
    // go stale (detached from their button) once the modal's scroll container moves under them.
    const scrollParent = formTopRef.current?.closest('.custom-scrollbars__content');
    if (!scrollParent) return;
    const closeAll = () => {
      setOpenDropdown(null);
      setScrollCloseTick((t) => t + 1);
    };
    scrollParent.addEventListener('scroll', closeAll);
    return () => scrollParent.removeEventListener('scroll', closeAll);
  }, []);

  const openCalendar = () => {
    setOpenDropdown('date');
    setCalendarState((s) => ({
      ...s,
      active: true,
      selectedDay: form.date ? time.parseCalendarDateString(form.date).toDate() : new Date(),
    }));
  };

  useEffect(() => {
    if (openDropdown !== 'date') setCalendarState((s) => (s.active ? { ...s, active: false } : s));
  }, [openDropdown]);

  const initiatorLabel =
    form.initiator === 'Organisation' ? teamName : form.initiator === 'Stakeholder' ? 'Stakeholder' : null;

  // DropdownComponent closes itself whenever its `mainItem` prop changes identity, so this must stay
  // referentially stable across unrelated re-renders (e.g. opening this dropdown) or it self-closes
  // on the same render that opens it, requiring a second click to actually keep it open.
  const typeMainItem = useCallback(
    () => (
      <span className={`hidden-lines hidden-one-line ${form.type ? '' : 'text-main-blue-40'}`}>
        {form.type || 'Select type'}
      </span>
    ),
    [form.type]
  );

  const initiatorMainItem = useCallback(
    () => (
      <span className={`hidden-lines hidden-one-line ${initiatorLabel ? '' : 'text-main-blue-40'}`}>
        {initiatorLabel || 'Select initiator'}
      </span>
    ),
    [initiatorLabel]
  );

  return (
    <>
      <div className='d-flex flex-column flex-sm-row mb-3' ref={formTopRef}>
        <div style={{ minWidth: 0, flex: '1 1 0%' }}>
          <label className='d-block mb-1 paragraph-p1 font-weight-bold'>Title</label>
          <input
            type='text'
            className='form-control border-general border-grey no-focus-outline text-main-blue'
            value={form.title}
            onChange={(e) => onChange('title', e.target.value)}
            autoFocus={autoFocusTitle}
          />
        </div>
        <div className='mt-3 mt-sm-0 ml-sm-3' style={{ minWidth: 0, flex: '1 1 0%' }}>
          <label className='d-block mb-1 paragraph-p1 font-weight-bold'>Date</label>
          <button
            type='button'
            className='form-control border-general border-grey text-left no-focus-outline d-flex align-items-center justify-content-between'
            onClick={openCalendar}
          >
            <span className={form.date ? 'text-main-blue' : 'text-main-blue-40'}>
              {form.date ? dayjs(form.date).format('D MMMM YYYY') : 'Select date'}
            </span>
            <span className='icon-calendar' />
          </button>
          <CalendarPopup
            showPopup={calendarState.active}
            calendarState={calendarState}
            setCalendarState={setCalendarState}
            ApplyCalendarFilters={() => onChange('date', dayjs(calendarState.selectedDay).format('YYYY-MM-DD'))}
            setShowPopup={(val) => setCalendarState((s) => ({ ...s, active: val }))}
            noFutureDatesAllowed={false}
            selectedMonth={calendarState.selectedDay ? dayjs(calendarState.selectedDay).toDate() : new Date()}
          />
        </div>
      </div>

      <div className='d-flex flex-column flex-sm-row mb-3'>
        <div className='shadow-dropdown' style={{ minWidth: 0, flex: '1 1 0%' }}>
          <label className='d-block mb-1 paragraph-p1 font-weight-bold'>Type</label>
          <DropdownComponent
            mainItem={typeMainItem}
            fixedDropdown
            waitOnOpen={false}
            mainItemClass='form-control border-general border-grey no-focus-outline interaction-form-select'
            dropdownClass='shadow-open-dropdown interaction-form-dropdown'
            fixedTopSpace={4}
            onOpen={() => setOpenDropdown('type')}
            closeSignal={scrollCloseTick}
          >
            <ul className='policy-list p-0 m-0' style={{ overflowY: 'auto', maxHeight: '200px' }}>
              {INTERACTION_TYPES.map((type) => (
                <li
                  key={type}
                  onClick={() => onChange('type', type)}
                  className='list-action-button py-2 px-3 flex-centered pointer'
                >
                  <span className='flex-grow-1'>{type === 'Events' ? 'Event' : type}</span>
                  {form.type === type && <span className='icon-tick paragraph-p1' />}
                </li>
              ))}
            </ul>
          </DropdownComponent>
        </div>
        <div className='mt-3 mt-sm-0 ml-sm-3 shadow-dropdown' style={{ minWidth: 0, flex: '1 1 0%' }}>
          <label className='d-block mb-1 paragraph-p1 font-weight-bold'>Initiator</label>
          <DropdownComponent
            mainItem={initiatorMainItem}
            fixedDropdown
            waitOnOpen={false}
            mainItemClass='form-control border-general border-grey no-focus-outline interaction-form-select'
            dropdownClass='shadow-open-dropdown interaction-form-dropdown'
            fixedTopSpace={4}
            onOpen={() => setOpenDropdown('initiator')}
            closeSignal={scrollCloseTick}
          >
            <ul className='policy-list p-0 m-0'>
              {[
                { value: 'Organisation', label: teamName },
                { value: 'Stakeholder', label: 'Stakeholder' },
              ].map(({ value, label }) => (
                <li
                  key={value}
                  onClick={() => onChange('initiator', value)}
                  className='list-action-button py-2 px-3 flex-centered pointer'
                >
                  <span className='flex-grow-1'>{label}</span>
                  {form.initiator === value && <span className='icon-tick paragraph-p1' />}
                </li>
              ))}
            </ul>
          </DropdownComponent>
        </div>
      </div>

      <div className='d-flex flex-column flex-sm-row mb-3'>
        <div className='d-flex flex-column' style={{ minWidth: 0, flex: '1 1 0%' }}>
          <div className='d-flex justify-content-between align-items-center mb-1' style={{ minHeight: 30 }}>
            <label className='paragraph-p1 font-weight-bold mb-0 text-truncate' style={{ minWidth: 0 }}>
              People from {organisationName}
            </label>
            <RemoveSelectionButton
              id='members-menu'
              count={form.members?.length ?? 0}
              singular='person'
              plural='people'
              onRemoveAll={() => onChange('members', [])}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
            />
          </div>
          <MembersSelect
            value={form.members ?? []}
            onChange={(selected) => onChange('members', selected)}
            placeholder='Add person'
            menuIsOpen={openDropdown === 'members'}
            onMenuOpen={() => setOpenDropdown('members')}
            onMenuClose={onMenuCloseFor('members')}
          />
        </div>
        <div className='mt-3 mt-sm-0 ml-sm-3 d-flex flex-column' style={{ minWidth: 0, flex: '1 1 0%' }}>
          <div className='d-flex justify-content-between align-items-center mb-1' style={{ minHeight: 30 }}>
            <label className='paragraph-p1 font-weight-bold mb-0'>Stakeholders</label>
            <RemoveSelectionButton
              id='stakeholders-menu'
              count={form.stakeholders?.length ?? 0}
              singular='stakeholder'
              plural='stakeholders'
              onRemoveAll={() => onChange('stakeholders', [])}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
            />
          </div>
          <PeopleAsyncSelect
            value={form.stakeholders ?? []}
            onChange={(selected) => onChange('stakeholders', selected ?? [])}
            placeholder='Add stakeholder'
            menuIsOpen={openDropdown === 'stakeholders'}
            onMenuOpen={() => setOpenDropdown('stakeholders')}
            onMenuClose={onMenuCloseFor('stakeholders')}
          />
        </div>
      </div>

      <div className='mb-3'>
        <label className='d-block mb-2 paragraph-p2 font-weight-bold'>Description</label>
        <div className='influence-description interaction-form-description w-100' style={{ display: 'flow-root' }}>
          <DescriptionTinyEditor
            value={form.description}
            onChangeFunction={(content) => onChange('description', content)}
            minHeight={200}
            maxHeight={300}
            hideSkeleton
            autoresize_bottom_margin={14}
            disableAutofocus
            toolbarLocation='bottom'
            colorPickerPosition='top'
            newline_behavior='block'
            additionalStyles='p { margin: 0; }'
          />
        </div>
      </div>
    </>
  );
};

export default InteractionFormFields;
